import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { RoleGuard } from "@/components/RoleGuard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { CardDestaque, COR_STATUS, PageHeader, SecaoCard } from "@/components/padroes";
import { StatusFolha } from "@/components/folha/StatusFolha";
import { Paginacao } from "@/components/Paginacao";
import { useAuth } from "@/context/AuthContext";
import { usePaginado } from "@/hooks/usePaginado";
import { api } from "@/lib/api";
import { formatCents, formatDateTime, formatMesRef } from "@/lib/format";
import type { FolhaDetalhe, FolhaResumo } from "@/data/folha";
import { toast } from "sonner";
import { ChevronRight, Loader2, Plus } from "lucide-react";

const competenciaAtual = () => {
  const hoje = new Date();
  return `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, "0")}`;
};

const RHFolhas = () => {
  const { user } = useAuth();
  const nav = useNavigate();
  const { items: folhas, total, page, pageSize, carregando, setPage } = usePaginado<FolhaResumo>("/folhas");
  const [novaOpen, setNovaOpen] = useState(false);
  const [competencia, setCompetencia] = useState(competenciaAtual());
  const [criando, setCriando] = useState(false);
  // Itens que impedem o fechamento da competência em andamento (vem do detalhe).
  const [pendenciasAtual, setPendenciasAtual] = useState<number | null>(null);

  const podeEditar = user?.role === "rh";

  // A lista vem da mais recente para a mais antiga: a primeira em rascunho é a
  // "em andamento" e sai da tabela para o card destaque (só na 1ª página).
  const atual = page === 1 ? folhas.find((f) => f.status === "rascunho") : undefined;
  const anteriores = atual ? folhas.filter((f) => f.id !== atual.id) : folhas;
  const atualId = atual?.id;

  useEffect(() => {
    if (!atualId) return;
    let cancelado = false;
    setPendenciasAtual(null);
    api
      .get<FolhaDetalhe>(`/folhas/${atualId}`)
      .then((d) => {
        if (cancelado) return;
        // Bloqueio sem lista de pessoas (ex.: falta a planilha da Amil) conta como 1 item.
        setPendenciasAtual(d.bloqueios.reduce((s, b) => s + Math.max(1, b.colaboradores.length), 0));
      })
      .catch(() => {
        // Sem o detalhe o card só não mostra as pendências.
      });
    return () => {
      cancelado = true;
    };
  }, [atualId]);

  const criar = async () => {
    setCriando(true);
    try {
      const folha = await api.post<FolhaResumo>("/folhas", { competencia });
      toast.success(`Competência ${formatMesRef(competencia)} aberta`);
      setNovaOpen(false);
      nav(`/rh/folha/${folha.id}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao abrir competência");
    } finally {
      setCriando(false);
    }
  };

  return (
    <RoleGuard role={["rh", "financeiro"]}>
      <AppLayout title="Folha de Pagamento">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 tabular-nums">
          <PageHeader
            titulo="Folha de Pagamento"
            subtitulo="Uma folha por competência: salário fixo, comissões, coparticipação Amil e empréstimos. Importe as planilhas, confira e feche."
            acoes={
              podeEditar && (
                <Button onClick={() => setNovaOpen(true)}>
                  <Plus />
                  Nova competência
                </Button>
              )
            }
          />

          {atual && (
            <CardDestaque
              to={`/rh/folha/${atual.id}`}
              rotulo="Em andamento · rascunho"
              corStatus={COR_STATUS.ambar}
              titulo={<span className="capitalize">{formatMesRef(atual.competencia)}</span>}
              acao="Abrir folha"
              metricas={[
                { rotulo: "Colaboradores", valor: atual.totalColaboradores },
                {
                  rotulo: "Líquido a pagar",
                  valor: <span className="text-primary-text">{formatCents(atual.totalLiquido)}</span>,
                },
                {
                  rotulo: "Pendências",
                  valor:
                    pendenciasAtual === null ? (
                      <span className="text-muted-foreground">—</span>
                    ) : pendenciasAtual === 0 ? (
                      <span className="text-success">Nenhuma</span>
                    ) : (
                      <span className="text-destructive">
                        {pendenciasAtual} {pendenciasAtual === 1 ? "item" : "itens"}
                      </span>
                    ),
                },
              ]}
            />
          )}

          <SecaoCard titulo={atual ? "Competências anteriores" : "Competências"} semPadding>
            <Table className="min-w-[760px]">
              <TableHeader>
                <TableRow>
                  <TableHead className="pl-5 sm:pl-6">Competência</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Colaboradores</TableHead>
                  <TableHead className="text-right">Total líquido</TableHead>
                  <TableHead>Fechada em</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {carregando && folhas.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="py-12 text-center text-muted-foreground">
                      <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                    </TableCell>
                  </TableRow>
                ) : anteriores.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="py-12 text-center text-muted-foreground">
                      {atual ? "Nenhuma competência anterior." : "Nenhuma competência aberta ainda."}
                    </TableCell>
                  </TableRow>
                ) : (
                  anteriores.map((f) => (
                    <TableRow
                      key={f.id}
                      className="cursor-pointer"
                      onClick={() => nav(`/rh/folha/${f.id}`)}
                    >
                      <TableCell className="pl-5 font-medium capitalize sm:pl-6">
                        {formatMesRef(f.competencia)}
                        {f.reabertaCount > 0 && (
                          <span className="ml-2 text-[11px] font-normal normal-case text-muted-foreground">
                            reaberta {f.reabertaCount}x
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <StatusFolha status={f.status} comPendencia={(f.pendenciasCadastro ?? 0) > 0} />
                      </TableCell>
                      <TableCell className="text-right">{f.totalColaboradores}</TableCell>
                      <TableCell className="text-right font-semibold">
                        {formatCents(f.totalLiquido)}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {f.fechadaEm ? formatDateTime(f.fechadaEm) : "—"}
                      </TableCell>
                      <TableCell className="pr-4 text-muted-foreground/70">
                        <ChevronRight className="h-4 w-4" />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </SecaoCard>

          {total > 0 && (
            <Paginacao
              page={page}
              pageSize={pageSize}
              total={total}
              onPage={setPage}
              carregando={carregando}
              rotulo="competências"
            />
          )}
        </div>
      </AppLayout>

      <Dialog open={novaOpen} onOpenChange={setNovaOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Nova competência</DialogTitle>
            <DialogDescription>
              A folha nasce em rascunho com todos os colaboradores ativos que têm salário fixo. Você
              importa as planilhas depois.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="nova-competencia">Competência</Label>
            <Input
              id="nova-competencia"
              type="month"
              value={competencia}
              onChange={(e) => setCompetencia(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNovaOpen(false)} disabled={criando}>
              Cancelar
            </Button>
            <Button onClick={() => void criar()} disabled={!competencia || criando}>
              {criando && <Loader2 className="animate-spin" />}
              Abrir competência
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </RoleGuard>
  );
};

export default RHFolhas;
